export enum AdType {
  // Level Up Rewards
  REROLL_REWARDS = 'reroll_rewards',
  EXTRA_CHOICE = 'extra_choice',
  
  // Gameplay Bonuses
  REVIVE_EARTH = 'revive_earth',
  DOUBLE_CREDITS = 'double_credits',
  TOWER_BOOST = 'tower_boost',
  PET_BOOST = 'pet_boost',
  
  // Currency Bonuses
  FREE_GOLD = 'free_gold',
  FREE_RUBY = 'free_ruby',
  FREE_EQUIPMENT = 'free_equipment',
  
  // Daily Bonuses
  DAILY_REWARD = 'daily_reward',
  STREAK_BONUS = 'streak_bonus',
}

export interface AdReward {
  type: string;
  amount?: number;
  duration?: number; // For temporary bonuses (ms)
  description: string;
  icon?: string;
}

export interface DailyReward {
  day: number;
  gold: number;
  ruby: number;
  equipment?: string; // Equipment ID
  description: string;
}

export interface AdSession {
  adsWatched: number;
  maxAdsPerSession: number;
  sessionStartTime: number;
  cooldownTime: number; // ms between ads
  lastAdTime: number;
}

export class AdManager {
  private static instance: AdManager;
  private adSession: AdSession;
  private dailyData: any;

  // Daily rewards schedule (7-day cycle)
  private static readonly DAILY_REWARDS: DailyReward[] = [
    { day: 1, gold: 100, ruby: 0, description: "Welcome Bonus!" },
    { day: 2, gold: 150, ruby: 5, description: "Day 2 Reward" },
    { day: 3, gold: 200, ruby: 0, equipment: 'basic_cannon', description: "Free Equipment!" },
    { day: 4, gold: 250, ruby: 10, description: "Mid-week Bonus" },
    { day: 5, gold: 300, ruby: 0, equipment: 'energy_crystal', description: "Crystal Reward!" },
    { day: 6, gold: 400, ruby: 15, description: "Weekend Bonus" },
    { day: 7, gold: 500, ruby: 25, equipment: 'legendary_core', description: "Weekly Jackpot!" },
  ];

  private constructor() {
    this.initializeAdSession();
    this.loadDailyData();
  }

  public static getInstance(): AdManager {
    if (!AdManager.instance) {
      AdManager.instance = new AdManager();
    }
    return AdManager.instance;
  }

  private initializeAdSession(): void {
    this.adSession = {
      adsWatched: 0,
      maxAdsPerSession: 10, // Limit ads per gameplay session
      sessionStartTime: Date.now(),
      cooldownTime: 30000, // 30 seconds between ads
      lastAdTime: 0,
    };
  }

  private loadDailyData(): any {
    const saved = localStorage.getItem('dailyData');
    if (saved) {
      this.dailyData = JSON.parse(saved);
    } else {
      this.dailyData = {
        lastLoginDate: null,
        currentStreak: 0,
        totalDaysLoggedIn: 0,
        lastDailyRewardClaimed: null,
      };
    }
    return this.dailyData;
  }

  private saveDailyData(): void {
    localStorage.setItem('dailyData', JSON.stringify(this.dailyData));
  }

  // Check if user can watch an ad
  public canWatchAd(): boolean {
    const now = Date.now();
    const timeSinceLastAd = now - this.adSession.lastAdTime;
    
    return (
      this.adSession.adsWatched < this.adSession.maxAdsPerSession &&
      timeSinceLastAd >= this.adSession.cooldownTime
    );
  }

  // Get cooldown remaining time
  public getAdCooldown(): number {
    const now = Date.now();
    const timeSinceLastAd = now - this.adSession.lastAdTime;
    return Math.max(0, this.adSession.cooldownTime - timeSinceLastAd);
  }

  // Simulate watching an ad (in real game, this would trigger actual ad)
  public async watchAd(adType: AdType): Promise<AdReward | null> {
    if (!this.canWatchAd()) {
      return null;
    }

    // Simulate ad loading/watching time
    await this.simulateAdWatch();

    this.adSession.adsWatched++;
    this.adSession.lastAdTime = Date.now();

    return this.generateAdReward(adType);
  }

  private async simulateAdWatch(): Promise<void> {
    // Simulate 2-5 second ad watch time
    const watchTime = 2000 + Math.random() * 3000;
    return new Promise(resolve => setTimeout(resolve, watchTime));
  }

  private generateAdReward(adType: AdType): AdReward {
    switch (adType) {
      case AdType.REROLL_REWARDS:
        return {
          type: 'reroll',
          description: 'Reroll level-up rewards!',
          icon: '🎲',
        };

      case AdType.EXTRA_CHOICE:
        return {
          type: 'extra_choice',
          description: 'Get an extra level-up choice!',
          icon: '➕',
        };

      case AdType.REVIVE_EARTH:
        return {
          type: 'revive',
          amount: 50, // 50% health restoration
          description: 'Revive Earth with 50% health!',
          icon: '💚',
        };

      case AdType.DOUBLE_CREDITS:
        return {
          type: 'double_credits',
          duration: 60000, // 1 minute
          description: '2x Energy Credits for 1 minute!',
          icon: '⚡',
        };

      case AdType.TOWER_BOOST:
        return {
          type: 'tower_boost',
          amount: 50, // 50% damage boost
          duration: 120000, // 2 minutes
          description: '+50% Tower Damage for 2 minutes!',
          icon: '🗼',
        };

      case AdType.PET_BOOST:
        return {
          type: 'pet_boost',
          amount: 100, // 100% attack speed boost
          duration: 90000, // 1.5 minutes
          description: '2x Pet Attack Speed for 1.5 minutes!',
          icon: '🐕',
        };

      case AdType.FREE_GOLD:
        const goldAmount = 50 + Math.floor(Math.random() * 100); // 50-150 gold
        return {
          type: 'gold',
          amount: goldAmount,
          description: `Free ${goldAmount} Gold!`,
          icon: '🪙',
        };

      case AdType.FREE_RUBY:
        const rubyAmount = 5 + Math.floor(Math.random() * 10); // 5-15 ruby
        return {
          type: 'ruby',
          amount: rubyAmount,
          description: `Free ${rubyAmount} Ruby!`,
          icon: '💎',
        };

      case AdType.FREE_EQUIPMENT:
        const equipmentTypes = ['basic_cannon', 'energy_crystal', 'shield_core', 'speed_booster'];
        const randomEquipment = equipmentTypes[Math.floor(Math.random() * equipmentTypes.length)];
        return {
          type: 'equipment',
          amount: 1,
          description: `Free Equipment: ${randomEquipment}!`,
          icon: '⚔️',
        };

      case AdType.DAILY_REWARD:
        const dailyReward = this.getCurrentDailyReward();
        return {
          type: 'daily',
          amount: dailyReward.gold,
          description: dailyReward.description,
          icon: '📅',
        };

      case AdType.STREAK_BONUS:
        const streakMultiplier = Math.min(this.dailyData.currentStreak, 7);
        return {
          type: 'streak',
          amount: streakMultiplier * 20, // 20 gold per streak day
          description: `${streakMultiplier}-day Streak Bonus!`,
          icon: '🔥',
        };

      default:
        return {
          type: 'unknown',
          description: 'Mystery reward!',
          icon: '❓',
        };
    }
  }

  // Daily login system
  public checkDailyLogin(): boolean {
    const today = new Date().toDateString();
    const lastLogin = this.dailyData.lastLoginDate;

    if (lastLogin !== today) {
      this.updateDailyStreak(lastLogin, today);
      this.dailyData.lastLoginDate = today;
      this.dailyData.totalDaysLoggedIn++;
      this.saveDailyData();
      return true; // First login today
    }

    return false; // Already logged in today
  }

  private updateDailyStreak(lastLogin: string | null, today: string): void {
    if (!lastLogin) {
      // First time login
      this.dailyData.currentStreak = 1;
      return;
    }

    const lastDate = new Date(lastLogin);
    const todayDate = new Date(today);
    const daysDiff = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff === 1) {
      // Consecutive day
      this.dailyData.currentStreak++;
    } else if (daysDiff > 1) {
      // Streak broken
      this.dailyData.currentStreak = 1;
    }
    // daysDiff === 0 means same day (shouldn't happen due to check above)
  }

  public canClaimDailyReward(): boolean {
    const today = new Date().toDateString();
    return this.dailyData.lastDailyRewardClaimed !== today;
  }

  public claimDailyReward(): DailyReward | null {
    if (!this.canClaimDailyReward()) {
      return null;
    }

    const reward = this.getCurrentDailyReward();
    this.dailyData.lastDailyRewardClaimed = new Date().toDateString();
    this.saveDailyData();

    return reward;
  }

  private getCurrentDailyReward(): DailyReward {
    const dayIndex = (this.dailyData.currentStreak - 1) % 7;
    return AdManager.DAILY_REWARDS[dayIndex] as DailyReward;
  }

  // Get available ad types for current context
  public getAvailableAds(context: 'levelup' | 'gameplay' | 'menu'): AdType[] {
    const ads: AdType[] = [];

    switch (context) {
      case 'levelup':
        ads.push(AdType.REROLL_REWARDS, AdType.EXTRA_CHOICE);
        break;

      case 'gameplay':
        ads.push(
          AdType.REVIVE_EARTH,
          AdType.DOUBLE_CREDITS,
          AdType.TOWER_BOOST,
          AdType.PET_BOOST
        );
        break;

      case 'menu':
        ads.push(
          AdType.FREE_GOLD,
          AdType.FREE_RUBY,
          AdType.FREE_EQUIPMENT,
          AdType.DAILY_REWARD,
          AdType.STREAK_BONUS
        );
        break;
    }

    // Filter out ads that can't be watched
    return this.canWatchAd() ? ads : [];
  }

  // Get daily login info
  public getDailyInfo(): any {
    return {
      currentStreak: this.dailyData.currentStreak,
      totalDaysLoggedIn: this.dailyData.totalDaysLoggedIn,
      canClaimDaily: this.canClaimDailyReward(),
      nextReward: this.getCurrentDailyReward(),
    };
  }

  // Get ad session info
  public getAdSessionInfo(): any {
    return {
      adsWatched: this.adSession.adsWatched,
      maxAds: this.adSession.maxAdsPerSession,
      cooldownRemaining: this.getAdCooldown(),
      canWatch: this.canWatchAd(),
    };
  }

  // Reset ad session (called when starting new game session)
  public resetAdSession(): void {
    this.initializeAdSession();
  }

  // Get formatted time string for cooldown
  public getCooldownString(): string {
    const cooldown = this.getAdCooldown();
    if (cooldown <= 0) return 'Ready';
    
    const seconds = Math.ceil(cooldown / 1000);
    return `${seconds}s`;
  }

  // Apply ad reward to user profile
  public static applyAdReward(reward: AdReward, userProfile: any): void {
    switch (reward.type) {
      case 'gold':
        userProfile.gold += reward.amount || 0;
        break;

      case 'ruby':
        userProfile.ruby += reward.amount || 0;
        break;

      case 'equipment':
        // Add equipment to user profile (simplified)
        if (!userProfile.ownedItems) userProfile.ownedItems = [];
        userProfile.ownedItems.push({
          id: 'basic_cannon', // reward equipment
          level: 1,
          obtainedAt: Date.now(),
        });
        break;

      case 'daily':
        userProfile.gold += reward.amount || 0;
        break;

      case 'streak':
        userProfile.gold += reward.amount || 0;
        break;

      // Temporary bonuses are handled by the game scene
      case 'reroll':
      case 'extra_choice':
      case 'revive':
      case 'double_credits':
      case 'tower_boost':
      case 'pet_boost':
        // These are handled by the calling scene
        break;
    }
  }
} 